import { ApiError } from "@porchpass/api-client";
import { isAutosaveFieldFailure } from "./autosaveFieldFailure";

/**
 * Pure classification + message-building for auto-save failures. The host
 * component decides the side effects (toast, lock-conflict dialog); this module
 * just answers "what kind of error is this?" and "what should the toast say?".
 */

export type SaveErrorKind =
  "lock-conflict" | "already-in-use" | "field-scoped" | "generic";

export function classifySaveError(error: unknown): SaveErrorKind {
  if (isAutosaveFieldFailure(error)) return "field-scoped";
  if (error instanceof ApiError) {
    if (error.status === 409) return "lock-conflict";
    if ((error.message ?? "").toLowerCase().includes("already in use")) {
      return "already-in-use";
    }
  }
  return "generic";
}

/**
 * Build the "Couldn't save: …" toast message for a generic save failure, or
 * null when there is nothing actionable to show (no failed fields). Lock
 * conflicts and "already in use" errors are surfaced elsewhere and should not
 * produce this toast.
 */
export function buildSaveErrorMessage(
  failedFields: Set<string> | undefined,
  getDisplayName: (fieldPath: string) => string,
): string | null {
  if (!failedFields || failedFields.size === 0) return null;
  const names = [...new Set(Array.from(failedFields).map(getDisplayName))];
  return `Couldn't save: ${names.join(", ")}. Please try again.`;
}
